import styled from '@emotion/styled';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { tokens, Pagination } from '@hotjar/hotsauce';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import EmptyStateImage from '@hotjar/shared-assets/images/empty-surveys-new.png';
import { useCallback, useMemo } from 'react';
import { keyBy } from 'lodash-es';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useTranslation } from '@hotjar/translations';
import { ResponsesList } from './ResponsesList/ResponsesList';
import { useSurveySorting } from '../../../../hooks/useSurveysSorting';
import { ResponsesTableControls } from '../ResponseTableControls';
import { NewSurveyMessage } from '../NewSurveyMessage';
import { truncateUuid } from '../../../../domain/Surveys/Questions';
import { SurveyView } from '../SurveyView';
import { useExportResponses } from '../../hooks/useExportResponses';
import { useDeleteSurveyResponse } from '../../../../hooks/useDeleteSurveyResponse';
import { EntityResponseDeleteModal } from '../../../common/EntityActions/EntityDeleteModal';
import { useTableColumnsOptions } from '../hooks/useTableColumnsOptions';
import type { ColumnOptions } from '../../../../domain/Surveys/Responses/SurveyResponsesColumns';
import { useResponsesViewRouting } from './useResponseViewRouting';
import { useLoadResponsesViewData } from './useLoadResponsesViewData';
import { useResponseId } from './useResponseId';
import { SurveyResponseModal } from '../../Responses/ResponseModal';
import { useScheduledTrigger } from '../../../../hooks/survey/useScheduledTriggersStore';
import { useSurveyResponsesViewReducer } from './useSurveyResponsesViewReducer';
import { SurveyResults } from '../SurveyResultsView/SurveyResultsView';
import { useMergedSurveyViewExperiment } from '../../hooks/useMergedSurveyViewExperiment';
import { ResultsQuestionSelector } from '../SurveyResultsView/ResultsQuestionSelector';
import { useSelectedQuestion } from '../SurveyResultsView/useSelectedQuestion';

const QuestionSelectorWrapper = styled.div`
  margin: ${tokens.spacer.x3} 0 ${tokens.spacer.x5} 0;
`;

const EmbeddableWidgetContainer = styled.div`
  min-width: 100%;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${tokens.spacer.x2};
`;

type SurveyIdStatus = 'Found' | 'NotFound' | 'Default' | 'Fetching';

export interface SurveyResponsesViewProps {
  surveyIdData: { value: undefined | number; status: SurveyIdStatus };
  navigateTo: (state: string) => void;
  columnOptions: ColumnOptions<string>;
  onColumnUpdated: (columns: ColumnOptions<string>) => void;
}

type ResponsesState = 'loading' | 'some' | 'none';

function getResponseState(loaded: boolean, responseCount: number | undefined): ResponsesState {
  if (!loaded || responseCount === undefined) return 'loading';
  if (responseCount === 0) return 'none';
  return 'some';
}

export const SurveyResponsesView = (): JSX.Element | null => {
  const { t } = useTranslation('feedback');
  const { coveragePercentage } = useTrafficCoverage();
  const { filters, pageSize, setPageSize, pageNumber, setPageNumber } =
    useSurveyResponsesViewReducer();
  const { sort, setSort, serverSortField } = useSurveySorting();

  const responseId = useResponseId();

  useScheduledTrigger('created_survey');

  const {
    isLoaded,
    site,
    survey,
    questions,
    hasAnyStatements,
    responses,
    totalResponseCount,
    filteredResponseCount,
    surveyId,
    surveyIdStatus,
  } = useLoadResponsesViewData(filters, pageNumber, pageSize);

  useResponsesViewRouting({
    siteId: site?.id,
    surveyId: survey?.id,
    enabled: surveyIdStatus === 'Default',
  });

  const {
    deleteResponse,
    isDeleteResponsePending,
    isDeleteResponseConfirmationRequired,
    onDeleteResponseCancel,
    onDeleteResponseConfirm,
  } = useDeleteSurveyResponse(surveyId);

  const { tableColumnsOptions, updateColumnsOptions } = useTableColumnsOptions(hasAnyStatements);

  const { beginExport, fileTooLargeModal } = useExportResponses(surveyId);

  const hasSurveyResponses = getResponseState(survey !== undefined, totalResponseCount);
  const hasPageResponses = getResponseState(responses !== undefined, responses?.length);
  const questionsById = useMemo(() => keyBy(questions, truncateUuid), [questions]);

  const onEditForwarding = useCallback(() => {
    if (!site || !survey) return;
    navigateTo(SURVEYS.EDIT.path, {
      surveyId: survey.id,
      activeFlowSection: 'forwarding',
      siteId: site.id,
    });
  }, [site, survey]);

  const onExportSelectedWrapper = useCallback(
    (exportType: 'csv' | 'xlsx') => {
      beginExport(exportType, serverSortField);
    },
    [beginExport, serverSortField],
  );

  const isInMergedViewExperiment = useMergedSurveyViewExperiment();

  const selectedQuestion = useSelectedQuestion(surveyId, true);

  return (
    <SurveyView view="responses" isLoaded={isLoaded} surveyId={surveyId}>
      {survey && (
        <>
          {isInMergedViewExperiment && (
            <QuestionSelectorWrapper>
              <ResultsQuestionSelector
                surveyId={survey.id}
                trackingEventName="Merged Results view variant question selected"
                allowAll
              />
            </QuestionSelectorWrapper>
          )}
          {isInMergedViewExperiment &&
            selectedQuestion !== undefined &&
            selectedQuestion !== 'ALL' && <SurveyResults />}

          {hasSurveyResponses === 'some' && responses && (
            <ResponsesTableControls
              onEditForwarding={onEditForwarding}
              onExportSelected={onExportSelectedWrapper}
              pageResponseCount={responses.length}
              survey={survey}
              totalCount={filteredResponseCount}
              columnOptions={tableColumnsOptions}
              onColumnUpdated={updateColumnsOptions}
            />
          )}
          {hasPageResponses === 'some' && (
            <>
              <ResponsesList
                surveyId={survey.id}
                sortState={sort}
                onSortChanged={setSort}
                questions={questions}
                responses={responses}
                onDeleteResponseClicked={deleteResponse}
                columnOptions={tableColumnsOptions}
                coveragePercentage={coveragePercentage}
                isExternalLinkSurvey={survey?.display_type === 'external_link'}
              />

              <PaginationContainer>
                <Pagination
                  currentPage={pageNumber}
                  totalCount={filteredResponseCount ?? 0}
                  pageSize={pageSize}
                  onPageChange={setPageNumber}
                  onPageSizeChange={setPageSize}
                />
              </PaginationContainer>
              <EmbeddableWidgetContainer id="embeddable-survey-responses" />
            </>
          )}

          {hasSurveyResponses === 'some' && hasPageResponses === 'none' && (
            <EmptyState
              title={t('survey-responses.empty-state.title')}
              subtitle={t('survey-responses.empty-state.subtitle')}
              img={EmptyStateImage}
            />
          )}

          {hasSurveyResponses === 'none' && site && (
            <NewSurveyMessage surveyId={survey.id} onEdit={onEditForwarding} />
          )}

          {responseId !== undefined && (
            <SurveyResponseModal
              responseId={responseId}
              surveyId={survey.id}
              questionsById={questionsById}
              responses={responses}
              coveragePercentage={coveragePercentage}
              isExternalLinkSurvey={survey?.display_type === 'external_link'}
              siteId={site?.id}
            />
          )}
          {fileTooLargeModal}
          <EntityResponseDeleteModal
            entityType="survey"
            isDeletePending={isDeleteResponsePending}
            isOpen={isDeleteResponseConfirmationRequired}
            onClose={onDeleteResponseCancel}
            onDelete={onDeleteResponseConfirm}
          />
        </>
      )}
    </SurveyView>
  );
};
